import { useState } from "react";
import { ChevronDown, HelpCircle } from "lucide-react";
import { EmptyState, SectionHeading } from "./ui";
import type { PortalPayload } from "../lib/api";

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <h3>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          className="flex w-full items-center justify-between gap-4 py-5 text-left transition-colors hover:text-wandor-prompt focus-visible:ring-2 focus-visible:ring-wandor-prompt/40"
        >
          <span className="text-base font-medium text-white md:text-lg">{question}</span>
          <ChevronDown
            className={`h-5 w-5 shrink-0 text-wandor-muted transition-transform duration-200 ${
              open ? "rotate-180" : ""
            }`}
            aria-hidden="true"
          />
        </button>
      </h3>
      {open && (
        <p className="whitespace-pre-line pb-5 pr-8 text-sm leading-relaxed text-wandor-muted">
          {answer}
        </p>
      )}
    </div>
  );
}

export default function FAQSection({ faqs }: { faqs: PortalPayload["faqs"] }) {
  return (
    <section id="faqs" className="mx-auto max-w-shell scroll-mt-24 px-5 py-24 md:px-10">
      <SectionHeading
        eyebrow="FAQs"
        title="Questions, answered"
        description="Everything students and the committee usually ask about the freshers' party."
      />

      <div className="mx-auto mt-10 max-w-3xl">
        {faqs.length === 0 ? (
          <EmptyState
            title="No FAQs published yet"
            body="The committee will add answers to common questions here."
          />
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] px-6 shadow-[0_8px_30px_rgba(0,0,0,0.05)]">
            {faqs.map((f) => (
              <FaqItem key={f.id} question={f.question} answer={f.answer} />
            ))}
          </div>
        )}

        <p className="mt-6 flex items-center gap-2 text-sm text-wandor-muted">
          <HelpCircle className="h-4 w-4 text-wandor-prompt" aria-hidden="true" />
          Still confused? Reach out to the organizing committee — details are shared in class groups.
        </p>
      </div>
    </section>
  );
}
